import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { REEMBOLSOS_POR_LIGA } from '../services/pedidoReembolsoService';
import type { PedidoReembolso, StatusReembolso } from '../types/pedidoReembolso';

// Espelha hooks/useFinanceiroLiga.ts (mesma fase — `no-cache`, derivados, retorno por objeto),
// mas as contagens por status são deliberadamente NÃO derivadas dos `pedidos` carregados: cada
// status do enum ganha sua própria consulta leve (`status: [X], size: 1`), lendo só `totalItems`.
// Contar sobre a página principal daria um número errado assim que houvesse mais de uma página —
// exatamente o defeito que esta PRD existe para eliminar (ver `## Notes` do plano da Phase 4).
// Por isso este arquivo não usa `.reduce`/`.filter` sobre `items` para produzir `contagens`.

const STATUSES: StatusReembolso[] = ['PENDENTE', 'APROVADO', 'RECUSADO', 'FINALIZADO'];

interface ReembolsosPorLigaConnection {
  items: PedidoReembolso[] | null;
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
}

interface ReembolsosPorLigaData {
  reembolsosPorLiga: ReembolsosPorLigaConnection | null;
}

interface ReembolsosPorLigaVars {
  ligaId: string;
  status?: StatusReembolso[];
  inicio?: string;
  fim?: string;
  page?: number;
  size?: number;
}

export interface ContagemStatusReembolso {
  status: string;
  total: number;
}

export interface UseReembolsosLigaResult {
  pedidos: PedidoReembolso[];
  contagens: ContagemStatusReembolso[];
  totalItems: number;
  filtroStatus: StatusReembolso | null;
  setFiltroStatus: (status: StatusReembolso | null) => void;
  loading: boolean;
  error: Error | undefined;
  refetch: () => void;
}

export function useReembolsosLiga(ligaId: string): UseReembolsosLigaResult {
  const [filtroStatus, setFiltroStatus] = useState<StatusReembolso | null>(null);

  const listaQuery = useQuery<ReembolsosPorLigaData, ReembolsosPorLigaVars>(REEMBOLSOS_POR_LIGA, {
    variables: {
      ligaId,
      status: filtroStatus ? [filtroStatus] : undefined,
      page: 0,
      size: 10,
    },
    skip: !ligaId,
    fetchPolicy: 'no-cache',
  });

  // Uma consulta leve por valor do enum — 4 requisições de `totalItems`, nunca contagem no
  // cliente sobre `listaQuery.data`.
  const pendenteQuery = useQuery<ReembolsosPorLigaData, ReembolsosPorLigaVars>(REEMBOLSOS_POR_LIGA, {
    variables: { ligaId, status: ['PENDENTE'], size: 1 },
    skip: !ligaId,
    fetchPolicy: 'no-cache',
  });
  const aprovadoQuery = useQuery<ReembolsosPorLigaData, ReembolsosPorLigaVars>(REEMBOLSOS_POR_LIGA, {
    variables: { ligaId, status: ['APROVADO'], size: 1 },
    skip: !ligaId,
    fetchPolicy: 'no-cache',
  });
  const recusadoQuery = useQuery<ReembolsosPorLigaData, ReembolsosPorLigaVars>(REEMBOLSOS_POR_LIGA, {
    variables: { ligaId, status: ['RECUSADO'], size: 1 },
    skip: !ligaId,
    fetchPolicy: 'no-cache',
  });
  const finalizadoQuery = useQuery<ReembolsosPorLigaData, ReembolsosPorLigaVars>(REEMBOLSOS_POR_LIGA, {
    variables: { ligaId, status: ['FINALIZADO'], size: 1 },
    skip: !ligaId,
    fetchPolicy: 'no-cache',
  });

  const pedidos = listaQuery.data?.reembolsosPorLiga?.items ?? [];
  const totalItems = listaQuery.data?.reembolsosPorLiga?.totalItems ?? 0;

  const contagens: ContagemStatusReembolso[] = [
    { status: 'PENDENTE', total: pendenteQuery.data?.reembolsosPorLiga?.totalItems ?? 0 },
    { status: 'APROVADO', total: aprovadoQuery.data?.reembolsosPorLiga?.totalItems ?? 0 },
    { status: 'RECUSADO', total: recusadoQuery.data?.reembolsosPorLiga?.totalItems ?? 0 },
    { status: 'FINALIZADO', total: finalizadoQuery.data?.reembolsosPorLiga?.totalItems ?? 0 },
  ];

  const loading =
    listaQuery.loading ||
    pendenteQuery.loading ||
    aprovadoQuery.loading ||
    recusadoQuery.loading ||
    finalizadoQuery.loading;

  const error =
    listaQuery.error ||
    pendenteQuery.error ||
    aprovadoQuery.error ||
    recusadoQuery.error ||
    finalizadoQuery.error;

  const refetch = () => {
    listaQuery.refetch();
    pendenteQuery.refetch();
    aprovadoQuery.refetch();
    recusadoQuery.refetch();
    finalizadoQuery.refetch();
  };

  return { pedidos, contagens, totalItems, filtroStatus, setFiltroStatus, loading, error, refetch };
}
